import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { useGetCurrentUserAsyncQuery } from '@mehir/api/backoffice-api';
import { endSession } from '@app/store';
import { tokenStore } from '@app/store/api-config';
import {
  selectAuth,
  selectUser,
  selectToken,
  selectIsAuthenticated,
  selectIsLoading,
  setUser,
  setToken,
} from '@app/store/auth-slice';

export const useAuth = () => {
  const dispatch = useAppDispatch();

  const auth = useAppSelector(selectAuth);
  const user = useAppSelector(selectUser);
  const token = useAppSelector(selectToken);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsLoading);

  // Fetch current user when token is available (skip only if no token)
  const {
    data: userData,
    isFetching: isFetchingUser,
    refetch: refetchUser,
  } = useGetCurrentUserAsyncQuery(undefined, {
    skip: !token,
  });

  const logout = useCallback(() => {
    endSession();
  }, []);

  const refreshUser = useCallback(() => {
    if (token) {
      refetchUser();
    }
  }, [token, refetchUser]);

  // Rehydrate the session from localStorage on boot. The auth slice is not
  // persisted, so this is the single path back into an existing session.
  useEffect(() => {
    const storedToken = tokenStore.getAccessToken();
    if (storedToken && !token) {
      dispatch(setToken(storedToken));
    }
  }, [dispatch, token]);

  // Update user from query result
  useEffect(() => {
    if (userData && token) {
      dispatch(setUser(userData));
    }
  }, [userData, token, dispatch]);

  // A 401 on /auth/me is handled centrally by the store's error middleware,
  // which ends the session — no local error branch needed here.

  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username || 'User';

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    auth,

    // True while a restored token is still being exchanged for a user, so
    // callers can show a loader instead of flashing the login screen.
    isBootstrapping: Boolean(token) && !user && isFetchingUser,

    logout,
    refreshUser,

    roles: user?.roles ?? [],
    userName: fullName,
    userEmail: user?.email || '',
  };
};
